/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";
import { Link } from "../components/ui/Link";
import { Pencil, Printer } from "lucide-react";
import {
  providerDetail,
  updateProvider,
  type ProviderDetail as Detail,
  type ProviderUpdate,
} from "../api/registry";
import { useAuth } from "../auth/AuthContext";
import { useChrome } from "../contexts/ChromeContext";
import { useToast } from "../contexts/ToastContext";
import { Badge } from "../components/ui/Badge";
import { Button } from "../components/ui/Button";
import { CopyableId } from "../components/ui/CopyableId";
import { ErrorMessage } from "../components/ui/ErrorMessage";
import { Input } from "../components/ui/Input";
import { Modal } from "../components/ui/Modal";
import { Select } from "../components/ui/Select";
import { Spinner } from "../components/ui/Spinner";
import { Facts, Section } from "../components/DetailBits";
import { canEditRegistry } from "../components/RegistryBits";
import { formatAddress, providerName } from "../lib/registry";
import { BusyLabel } from "../components/ui/BusyLabel";

export function ReferralProviderDetail() {
  const { id } = useParams();
  const { hasRole } = useAuth();
  const [detail, setDetail] = useState<Detail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);

  const refresh = useCallback(async () => {
    if (id === undefined) {
      return;
    }
    const result = await providerDetail(id);
    if (result.ok) {
      setDetail(result.value);
      setError(null);
    } else {
      setError(result.error.message);
    }
  }, [id]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const name =
    detail === null ? undefined : providerName(detail.provider.firstName, detail.provider.lastName);
  useChrome(
    { back: { to: "/referrals/providers", label: "Providers" }, title: name },
    [name],
  );

  if (detail === null && error === null) {
    return (
      <div className="grid h-full place-items-center">
        <Spinner size="lg" />
      </div>
    );
  }
  if (detail === null) {
    return <ErrorMessage message={error} className="mb-0" />;
  }

  const provider = detail.provider;
  const activity = provider.referralActivity;
  const allTime = provider.clientStats.find((stats) => stats.timeframe === "ALL_TIME");
  const monthly = provider.clientStats.find((stats) => stats.timeframe === "MONTHLY");
  return (
    <div className="space-y-6">
      <header className="enter flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl leading-tight text-text-primary sm:text-[1.75rem]">
            {providerName(provider.firstName, provider.lastName)}
          </h1>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {detail.organizationName !== null && provider.organizationId !== null ? (
              <Link
                to={`/referrals/organizations/${provider.organizationId}`}
                className="text-sm text-accent underline-offset-2 hover:underline"
              >
                {detail.organizationName}
              </Link>
            ) : (
              <span className="text-sm text-text-tertiary">No organization</span>
            )}
            {provider.receiveUpdates && <Badge tone="success">Receives updates</Badge>}
            <CopyableId label="Provider" value={String(provider.id)} />
          </div>
        </div>
        {canEditRegistry(hasRole) && (
          <Button variant="secondary" size="md" onClick={() => setEditing(true)}>
            <Pencil size={15} />
            Edit
          </Button>
        )}
      </header>

      <ErrorMessage message={error} className="mb-0" />

      <div className="grid gap-4 lg:grid-cols-2">
        <Section title="Contact">
          <Facts
            facts={[
              ["Email", provider.email ?? "—"],
              ["Phone", provider.phoneNumber ?? "—"],
              [
                "Fax",
                provider.faxNumber ? (
                  <span key="fax" className="inline-flex flex-wrap items-center gap-2">
                    {provider.faxNumber}
                    <Badge tone="neutral">
                      <Printer size={10} className="mr-1 inline-block" />
                      Used for fax sends
                    </Badge>
                  </span>
                ) : (
                  "—"
                ),
              ],
              ["Address", formatAddress(detail.address) ?? "—"],
            ]}
          />
        </Section>

        <Section title="Activity">
          <Facts
            facts={[
              [
                "Last referral",
                activity.lastReferralAt !== null
                  ? new Date(activity.lastReferralAt).toLocaleDateString(undefined, {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })
                  : "None yet",
              ],
              [
                "Referrals sent",
                `${activity.allTimeCount} all time · ${activity.monthlyCount} last 30 days`,
              ],
              [
                "Clients linked",
                `${allTime?.totalClientCount ?? 0} all time · ${monthly?.totalClientCount ?? 0} last 30 days`,
              ],
              [
                "Active in care",
                `${allTime?.activeClientCount ?? 0} all time · ${monthly?.activeClientCount ?? 0} last 30 days`,
              ],
            ]}
          />
        </Section>

        <Section title="Referrals" className="lg:col-span-2">
          {activity.allTimeCount === 0 ? (
            <p className="text-sm text-text-tertiary">No referrals from this provider yet.</p>
          ) : (
            <Link
              className="text-sm text-accent underline-offset-2 hover:underline"
              to={`/referrals?providerId=${detail.provider.id}`}
            >
              See all {activity.allTimeCount} referrals from this provider
            </Link>
          )}
        </Section>
      </div>

      {editing && (
        <EditProviderModal
          detail={detail}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            void refresh();
          }}
        />
      )}
    </div>
  );
}

function EditProviderModal({
  detail,
  onClose,
  onSaved,
}: {
  detail: Detail;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { showToast } = useToast();
  const provider = detail.provider;
  const [firstName, setFirstName] = useState(provider.firstName ?? "");
  const [lastName, setLastName] = useState(provider.lastName ?? "");
  const [email, setEmail] = useState(provider.email ?? "");
  const [phoneNumber, setPhoneNumber] = useState(provider.phoneNumber ?? "");
  const [faxNumber, setFaxNumber] = useState(provider.faxNumber ?? "");
  const [receiveUpdates, setReceiveUpdates] = useState(provider.receiveUpdates);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setBusy(true);
    setError(null);
    const body: ProviderUpdate = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim() === "" ? null : email.trim(),
      phoneNumber: phoneNumber.trim() === "" ? null : phoneNumber.trim(),
      faxNumber: faxNumber.trim() === "" ? null : faxNumber.trim(),
      receiveUpdates,
      address: detail.address,
    };
    const result = await updateProvider(provider.id, body);
    setBusy(false);
    if (!result.ok) {
      setError(result.error.message);
      return;
    }
    showToast("success", "Provider updated");
    onSaved();
  }

  return (
    <Modal isOpen onClose={onClose} title="Edit provider" size="sm" asSheet={false}>
      <div className="space-y-4 px-6 py-5">
        <ErrorMessage message={error} className="mb-0" />
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="First name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
          <Input label="Last name" value={lastName} onChange={(e) => setLastName(e.target.value)} />
        </div>
        <Input label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        <Input
          label="Phone"
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value)}
        />
        <Input
          label="Fax (referral updates go here)"
          value={faxNumber}
          onChange={(e) => setFaxNumber(e.target.value)}
        />
        <Select
          label="Receives updates"
          value={receiveUpdates ? "yes" : "no"}
          onChange={(value) => setReceiveUpdates(value === "yes")}
          options={[
            { value: "yes", label: "Yes" },
            { value: "no", label: "No" },
          ]}
        />
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" size="sm" disabled={busy} onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={busy || firstName.trim() === "" || lastName.trim() === ""}
            onClick={() => void save()}
          >
            <BusyLabel busy={busy} busyText="Saving…">
              Save
            </BusyLabel>
          </Button>
        </div>
      </div>
    </Modal>
  );
}
